import React, { useContext, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
//@ts-ignore
import 'swiper/css';
//@ts-ignore
import 'swiper/css/effect-fade';
//@ts-ignore
import 'swiper/css/pagination';
import { Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { Box, Typography, Button, Stack, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import OAuth2 from './OAuth2';
import { getTranslation, CONSTANTS } from 'common';
import { useForm, useTranslations } from 'hooks';
import { isEmail, isRequired, Validation } from 'hooks/useForm';
import { noSpecialChars } from 'common/utilities';
import { noTokenApi, fetchMe } from 'api';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from 'contexts';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';
import useSnackbar from 'hooks/useSnackbar';


const LandingPageCarousel: React.FC<{ lang?: string }> = ({ lang }) => {
  useEffect(() => {
    if (lang) {
      // console.log("LandingPageCarousel lang:", lang);
    }
  }, [lang]);

  //this use effects just enlarges the dot of the swiper of the selected image, deleting this does not change a thing
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
    .swiper-pagination-bullet {
      width: 8px;
      height: 8px;
      background: #d1d5db;
      opacity: 1;
      transition: all 0.3s ease;
    }

    .swiper-pagination-bullet-active {
      background: #667eea;
      width: 24px;
      border-radius: 4px;
    }

    .swiper-pagination {
      margin-bottom: 0;
    }

    @media (max-width: 600px) {
      .swiper-pagination {
        margin-bottom: 35px;
      }
    }
  `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const translations = useTranslations();
  const navigate = useNavigate();
  const { sendSnack } = useSnackbar();
  const slides = [
    {
      highlightedTitle: getTranslation(translations, "carousel.title_1"),
      description: getTranslation(translations, "carousel.description_1"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_2"),
      description: getTranslation(translations, "carousel.description_2"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_3"),
      description: getTranslation(translations, "carousel.description_3"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_4"),
      description: getTranslation(translations, "carousel.description_4"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_5"),
      description: getTranslation(translations, "carousel.description_5"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_6"),
      description: getTranslation(translations, "carousel.description_6"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_7"),
      description: getTranslation(translations, "carousel.description_7"),
    },
    {
      highlightedTitle: getTranslation(translations, "carousel.title_8"),
      description: getTranslation(translations, "carousel.description_8"),
    },
  ];
  const validations: Validation[] = [
    ({ email }) =>
      isRequired(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ email }) =>
      isEmail(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ email }) =>
      noSpecialChars(email) || {
        email: getTranslation(translations, "register.valid.Error_invalid_chars"),
      },
  ];
  const { values, errors, touched, changeHandler, isValid } = useForm(
    { email: "" },
    validations
  );
  const emailInputRef = useRef<HTMLInputElement>(null);
  const submitEmail = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isValid) {
      sendSnack({ type: "error", message: getTranslation(translations, "register.valid.Error_after_at") });
      return;
    }
    const response = await noTokenApi.get(
      `users/emailinplatform/${values.email}`
    );
    if (response.status === 200) {
      navigate({
        pathname: response.data.mailexist ? "/login" : "/registration",
        search: createSearchParams({ email: values.email }).toString(),
      });
    }
  };
  const {
    actions: { setRegisterUser, setUser },
  } = useContext(AuthContext);
  const enterWithGoogle = async (token: string, registrated: boolean) => {
    try {
      sessionStorage.setItem(CONSTANTS.STORE_JWT, token);
      let result = await fetchMe(token);
      if (registrated) {
        //se già registrato, vai alla dashboard
        setUser({
          jwt: token,
          userId: result.data.user.id,
          email: result.data.user.email,
          username: result.data.user.username,
          firstname: result.data.user.firstname,
          lastname: result.data.user.lastname,
          picture: result.data.user.picture,
          linkprofile: result.data.user.linkprofile,
          subscriptiontype: result.data.user.subscriptiontype,
          i18n: result.data.user.i18n,
          dob: result.data.user.dob,
        });
        navigate("/dashboard/organizations");
      } else {
        //se nuovo, imposta i dati per la registrazione
        setRegisterUser({
          userId: result.data.user.id,
          jwt: token,
          ...result.data.user,
        });
        navigate("/organization");
      }
    } catch (error) {
      console.error("Error during Google login:", error);
    }
  };

  return (
    <Box id="carousel" sx={{
      width: '100%',
      height: 'calc(100dvh - 150px)',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: (theme) =>
        theme.palette.mode === "dark" ? theme.palette.background.default : '#f5f5f7',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      mt: { xs: '-45px', md: 0 },
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '900px',
        px: 3,
        textAlign: 'center',
      }}>
        <Swiper
          modules={[Pagination, Autoplay, EffectFade]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          effect="fade"
          fadeEffect={{
            crossFade: true
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          loop={true}
          style={{ width: '100%' }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <Box sx={{ py: 4 }}>
                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    mt: { xs: 10, md: 0 },
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    color: 'text.primary',
                    mb: 2,
                    minHeight: { xs: '120px', md: '140px' }
                  }}
                >
                  {slide.highlightedTitle && (
                    <Box component="span" sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      {slide.highlightedTitle}
                    </Box>
                  )}
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    lineHeight: 1.6,
                    minHeight: { xs: '80px', md: '60px' },
                    paddingBottom: 10
                  }}
                >
                  {slide.description}
                </Typography>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* roba fuori dallo swiper */}
      <Box sx={{ textAlign: 'center', width: "auto", mx: 'auto' }} id="oauth2-box">
        <Stack
          component="form"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          width="100%"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <TextField
            inputRef={emailInputRef}
            size="small"
            id="welcome-email"
            label={getTranslation(translations, "user.email.label")}
            value={values.email}
            onChange={({ target }) => changeHandler("email", target.value)}
            // error={Boolean(touched.email && errors.email)}
            // helperText={(touched.email && errors.email) || ' '}
            // FormHelperTextProps={{
            //   sx: { fontSize: 11, width: "100%", ml: 0 },
            // }}
            type="email"
            aria-controls="email"
            sx={{
              height: 37,
              backgroundColor: "background.paper",
              borderColor: "primary",
              "& .MuiInputLabel-root": {
                color: "text.secondary",
              },
              "& .MuiOutlinedInput-input": {
                color: "text.primary",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "& input:-webkit-autofill": {
                WebkitBoxShadow: (theme) => `0 0 0 100px ${theme.palette.background.paper} inset`,
                WebkitTextFillColor: (theme) => theme.palette.text.primary,
                caretColor: (theme) => theme.palette.text.primary,
                transition: "background-color 5000s ease-in-out 0s",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": { //FlowentiInput.tsx
                borderColor: "primary.main",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
          />
          <FlowentiButton
            size="small"
            id="welcome-button"
            type="submit"
            variant="contained"
            onClick={submitEmail}
          >
            {getTranslation(translations, "register.label.button_register_is_free")}
          </FlowentiButton>
          {/* {process.env.REACT_APP_ENV === "development" && */}
          <OAuth2
            login={false}
            onGoogleConfirm={enterWithGoogle}
            showLoginButton={false}
          />
          {/* } */}
        </Stack>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          mt: 4
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(84, 189, 193, 0.16)" : '#d1fae5',
            borderRadius: '6px',
            width: 'fit-content',
          }}>
            <CheckCircleIcon sx={{ color: (theme) => theme.palette.mode === "dark" ? "primary.main" : 'green', fontSize: '1.2rem' }} />
            <Typography
              sx={{
                color: (theme) => theme.palette.mode === "dark" ? "primary.main" : 'green',
                fontSize: '0.95rem',
                fontWeight: 500,
                padding: "4px 12px",
              }}
            >
              {getTranslation(translations, "carousel.free_forever_plan")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPageCarousel;
